import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import API_URL from '../utils/apiConfig';
import analyticsService from '../services/analytics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState(null);
  
  useEffect(() => {
    // Set up axios interceptor for email verification errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle email verification errors globally
        if (error.response?.status === 403 && error.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
          console.log('🔒 Global interceptor: Email verification required');
          setEmailVerificationError(error.response.data);
          // Don't redirect here, let the ProtectedRoute handle it
        }
        return Promise.reject(error);
      }
    );

    // Cleanup function to remove interceptor
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    // Check for existing user session on load
    const checkLoggedIn = async () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          
          // Verify token is still valid with the backend
          if (userData.token) {
            try {
              const response = await axios.get(`${API_URL}/auth/user`, {
                headers: {
                  Authorization: `Bearer ${userData.token}`
                }
              });
              
              // Update user data with the most recent from server
              const updatedUserData = {
                ...userData,
                ...response.data
              };
              
              setUser(updatedUserData);
              localStorage.setItem('user', JSON.stringify(updatedUserData));
              
              // Check onboarding status
              const shouldShowOnboarding = !updatedUserData.onboardingCompleted && 
                                         (updatedUserData.onboardingViewCount || 0) < 3;
              console.log('🔍 AuthContext: Checking onboarding status:', {
                onboardingCompleted: updatedUserData.onboardingCompleted,
                onboardingViewCount: updatedUserData.onboardingViewCount,
                onboardingSteps: updatedUserData.onboardingSteps,
                needsOnboarding: shouldShowOnboarding
              });
              setNeedsOnboarding(shouldShowOnboarding);
              
              // Also store token separately for easier access by other services
              localStorage.setItem('token', userData.token);
            } catch (error) {
              console.error('Token validation failed:', error);
              // Don't clear user data if it's just an email verification error
              if (!(error.response?.status === 403 && error.response?.data?.code === 'EMAIL_NOT_VERIFIED')) {
                localStorage.removeItem('user');
                localStorage.removeItem('token');
              }
            }
          }
        } catch (error) {
          console.error('Error parsing stored user:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    
    checkLoggedIn();
  }, []);
  
  const login = async (userData, token) => {
    try {
      console.log('Processing login with user data and token...');
      
      // Create a complete user object
      const completeUserData = {
        ...userData,
        token
      };
      
      console.log('Login successful, user data:', completeUserData);
      
      // Save to state and localStorage
      setUser(completeUserData);
      localStorage.setItem('user', JSON.stringify(completeUserData));
      
      // Set onboarding status based on completion and view count
      const shouldShowOnboarding = !completeUserData.onboardingCompleted && 
                                   (completeUserData.onboardingViewCount || 0) < 3;
      setNeedsOnboarding(shouldShowOnboarding);
      
      // Store token separately for easier access by other services
      localStorage.setItem('token', token);
      
      // Track successful login
      analyticsService.trackAuth('login', userData.authMethod || 'unknown', true, {
        user_plan: userData.paymentStatus || 'unknown',
        has_workspace: !!userData.defaultWorkspaceId,
        onboarding_completed: userData.onboardingCompleted || false
      });
      
      return completeUserData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithGoogle = async (googleCredential) => {
    try {
      console.log('Processing Google login...');
      // Send the Google token to our backend
      const response = await axios.post(`${API_URL}/auth/google`, {
        token: googleCredential.credential
      });
      
      // Get user data and JWT token from our backend
      const { token, user: userData, needsOnboarding: onboardingRequired } = response.data;
      
      // Create a complete user object
      const completeUserData = {
        ...userData,
        token,
        picture: userData.picture || googleCredential.picture
      };
      
      console.log('Google login successful, user data:', completeUserData);
      
      // Use the general login function to save the data
      return await login(completeUserData, token);
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const signup = async (googleCredential) => {
    try {
      console.log('Processing Google signup...');
      // Send the Google token to our backend signup endpoint
      const response = await axios.post(`${API_URL}/auth/google-signup`, {
        token: googleCredential.credential
      });
      
      // Get user data and JWT token from our backend
      const { token, user: userData, needsOnboarding: onboardingRequired } = response.data;
      
      // Create a complete user object
      const completeUserData = {
        ...userData,
        token,
        picture: userData.picture || googleCredential.picture
      };
      
      console.log('Signup successful, user data:', completeUserData);
      
      // Save to state and localStorage
      setUser(completeUserData);
      localStorage.setItem('user', JSON.stringify(completeUserData));
      
      // Set onboarding status based on completion and view count
      const shouldShowOnboarding = !completeUserData.onboardingCompleted && 
                                   (completeUserData.onboardingViewCount || 0) < 3;
      setNeedsOnboarding(shouldShowOnboarding);
      
      // Store token separately for easier access by other services
      localStorage.setItem('token', token);
      
      return completeUserData;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const emailSignup = async (signupData) => {
    try {
      console.log('Processing email signup...');
      // Send the signup data to our backend email signup endpoint
      const response = await axios.post(`${API_URL}/auth/email-signup`, signupData);
      
      // Get user data and JWT token from our backend
      const { token, user: userData, needsOnboarding: onboardingRequired, needsEmailVerification } = response.data;
      
      // Create a complete user object
      const completeUserData = {
        ...userData,
        token,
        needsEmailVerification
      };
      
      console.log('Email signup successful, user data:', completeUserData);
      
      // Save to state and localStorage
      setUser(completeUserData);
      localStorage.setItem('user', JSON.stringify(completeUserData));
      
      // Set onboarding status based on completion and view count
      const shouldShowOnboarding = !completeUserData.onboardingCompleted && 
                                   (completeUserData.onboardingViewCount || 0) < 3;
      setNeedsOnboarding(shouldShowOnboarding);
      
      // Store token separately for easier access by other services
      localStorage.setItem('token', token);
      
      // Clear any previous email verification errors
      setEmailVerificationError(null);
      
      // Track successful signup
      analyticsService.trackAuth('sign_up', 'email', true, {
        requires_email_verification: !!completeUserData.needsEmailVerification,
        onboarding_required: shouldShowOnboarding
      });
      
      return completeUserData;
    } catch (error) {
      console.error('Email signup error:', error);
      throw error;
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      console.log('Processing email login...');
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      // Get user data and JWT token from our backend
      const { token, user: userData, needsOnboarding: onboardingRequired } = response.data;
      
      // Create a complete user object
      const completeUserData = {
        ...userData,
        token
      };
      
      console.log('Email login successful, user data:', completeUserData);
      
      // Use the general login function to save the data
      return await login(completeUserData, token);
    } catch (error) {
      console.error('Email login error:', error);
      throw error;
    }
  };

  const verifyEmail = async (token) => {
    try {
      console.log('Verifying email...');
      const response = await axios.post(`${API_URL}/auth/verify-email`, {
        token
      });
      
      // Update user data to reflect verified status
      if (user) {
        const updatedUserData = {
          ...user,
          emailVerified: true
        };
        setUser(updatedUserData);
        localStorage.setItem('user', JSON.stringify(updatedUserData));
      }
      
      // Clear any email verification errors
      setEmailVerificationError(null);
      
      return response.data;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      console.log('Requesting password reset...');
      const response = await axios.post(`${API_URL}/auth/request-password-reset`, {
        email
      });
      
      return response.data;
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  };

  const resetPassword = async (token, password) => {
    try {
      console.log('Resetting password...');
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        password
      });
      
      return response.data;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const resendEmailVerification = async (email) => {
    try {
      console.log('Resending email verification...');
      const response = await axios.post(`${API_URL}/auth/resend-verification`, {
        email
      });
      
      return response.data;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  };
  
  const logout = () => {
    // Track logout
    if (user) {
      analyticsService.trackAuth('logout', user.authMethod || 'unknown', true, {
        session_duration_minutes: user.lastLoginAt ? 
          Math.round((Date.now() - new Date(user.lastLoginAt).getTime()) / 60000) : null
      });
    }
    
    setUser(null);
    setNeedsOnboarding(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API_URL}/users/profile`, profileData, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      const updatedUser = response.data.user;
      const completeUserData = {
        ...user,
        ...updatedUser
      };
      
      setUser(completeUserData);
      localStorage.setItem('user', JSON.stringify(completeUserData));
      
      return completeUserData;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  // Complete onboarding step
  const completeOnboardingStep = async (step, data) => {
    try {
      const response = await axios.post(`${API_URL}/users/onboarding`, {
        step,
        data
      }, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      const updatedUser = response.data.user;
      const completeUserData = {
        ...user,
        ...updatedUser
      };
      
      setUser(completeUserData);
      localStorage.setItem('user', JSON.stringify(completeUserData));
      
      // Update onboarding status
      const shouldShowOnboarding = !updatedUser.onboardingCompleted && 
                                   (updatedUser.onboardingViewCount || 0) < 3;
      console.log('🔄 AuthContext: Onboarding step completed:', {
        step,
        data,
        onboardingCompleted: updatedUser.onboardingCompleted,
        onboardingViewCount: updatedUser.onboardingViewCount,
        onboardingSteps: updatedUser.onboardingSteps,
        needsOnboarding: shouldShowOnboarding
      });
      setNeedsOnboarding(shouldShowOnboarding);
      
      return completeUserData;
    } catch (error) {
      console.error('Onboarding step completion error:', error);
      throw error;
    }
  };

  // Refresh user data from backend
  const refreshUserData = async () => {
    try {
      const token = getToken();
      if (!token) return null;

      const response = await axios.get(`${API_URL}/auth/user`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const updatedUserData = {
        ...user,
        ...response.data
      };
      
      setUser(updatedUserData);
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      
      // Update onboarding status
      const shouldShowOnboarding = !updatedUserData.onboardingCompleted && 
                                   (updatedUserData.onboardingViewCount || 0) < 3;
      setNeedsOnboarding(shouldShowOnboarding);
      
      return updatedUserData;
    } catch (error) {
      // Handle email verification errors specifically
      if (error.response?.status === 403 && error.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        console.log('🔒 Email verification required for user access');
        // Don't logout, just let the error bubble up to be handled by the caller
        throw error;
      }
      
      console.error('Error refreshing user data:', error);
      throw error;
    }
  };

  // Check if user needs email verification
  const needsEmailVerification = () => {
    if (!user) return false;
    
    // Google users don't need additional email verification
    if (user.googleId) return false;
    
    // Non-Google users need email verification
    return !user.emailVerified;
  };

  // Mark onboarding as complete
  const completeOnboarding = () => {
    setNeedsOnboarding(false);
  };

  // Increment onboarding view count
  const incrementOnboardingViewCount = async () => {
    try {
      const response = await axios.post(`${API_URL}/users/increment-onboarding-view`, {}, {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      
      const updatedUser = {
        ...user,
        onboardingViewCount: response.data.onboardingViewCount
      };
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Update needsOnboarding based on server response
      setNeedsOnboarding(response.data.needsOnboarding);
      
      return response.data;
    } catch (error) {
      console.error('Error incrementing onboarding view count:', error);
      throw error;
    }
  };
  
  /**
   * Get the current authentication token
   * @returns {string|null} The JWT token if available, null otherwise
   */
  const getToken = () => {
    // First check localStorage
    const token = localStorage.getItem('token');
    if (token) return token;
    
    // Fallback to user object
    if (user && user.token) {
      // Store token in localStorage for future use
      localStorage.setItem('token', user.token);
      return user.token;
    }
    
    return null;
  };
  
  /**
   * Check if the user is authenticated
   * @returns {boolean} Whether the user is authenticated
   */
  const isAuthenticated = () => {
    return !!getToken();
  };
  
  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      loginWithGoogle,
      loginWithEmail,
      signup,
      emailSignup,
      verifyEmail,
      requestPasswordReset,
      resetPassword,
      resendEmailVerification,
      logout, 
      loading, 
      getToken,
      isAuthenticated: isAuthenticated(),
      needsOnboarding,
      needsEmailVerification,
      updateProfile,
      completeOnboardingStep,
      completeOnboarding,
      refreshUserData,
      incrementOnboardingViewCount
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;